import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Mail, KeyRound } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const Code = ({ value, onChange }) => (
  <InputOTP maxLength={6} value={value} onChange={onChange} data-testid="login-otp-input">
    <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}</InputOTPGroup>
  </InputOTP>
);

export default function Login() {
  const { setAuthUser } = useApp();
  const [step, setStep] = useState("password");
  const [f, setF] = useState({ email: "", password: "", code: "", newPw: "" });
  const [ch, setCh] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const run = async (fn) => {
    setBusy(true); setErr("");
    try { await fn(); } catch (e) { setErr(await errMsg(e)); }
    setBusy(false);
  };
  const login = (e) => { e.preventDefault(); run(async () => { const r = await api.post("/auth/login", { email: f.email, password: f.password }); setCh(r.data); setStep("code"); setF((s) => ({ ...s, code: "" })); }); };
  const verify = (e) => { e.preventDefault(); run(async () => { const r = await api.post("/auth/verify", { challenge_id: ch.challenge_id, code: f.code }); setAuthUser(r.data); }); };
  const resend = () => run(async () => { const r = await api.post("/auth/resend-code", { challenge_id: ch.challenge_id }); setCh({ ...ch, ...r.data }); toast.success("A new code was sent"); });
  const forgot = () => run(async () => { if (!f.email) throw new Error("Enter your email first"); const r = await api.post("/auth/forgot-password", { email: f.email }); setCh(r.data); setStep("reset"); toast.success(r.data.message); });
  const reset = (e) => { e.preventDefault(); run(async () => { await api.post("/auth/reset-password", { challenge_id: ch.challenge_id, code: f.code, new_password: f.newPw }); toast.success("Password updated — please sign in"); setStep("password"); setF((s) => ({ ...s, password: "", code: "", newPw: "" })); }); };
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="grid-bg relative hidden flex-col justify-between border-r p-12 lg:flex">
        <div className="font-display text-xl font-extrabold">ROXY<span className="text-primary"> LEDGER</span> <span className="text-teal-500">AI</span></div>
        <div>
          <h1 className="text-5xl font-extrabold leading-tight">Every bank entry,<br />accounted for.</h1>
          <p className="mt-4 max-w-md text-muted-foreground">Statement extraction, zero-missing-entry reconciliation and TallyPrime exports — for approved staff only.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4 text-emerald-500" />Two-step verification · encrypted storage · full audit trail</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          {step === "password" && (
            <form onSubmit={login} className="space-y-5" data-testid="login-form">
              <div><h2 className="text-3xl font-extrabold">Sign in</h2><p className="mt-1 text-sm text-muted-foreground">Accounts are created by your administrator.</p></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" autoComplete="username" value={f.email} onChange={set("email")} required data-testid="login-email-input" /></div>
              <div className="space-y-1.5"><Label>Password</Label><Input type="password" autoComplete="current-password" value={f.password} onChange={set("password")} required data-testid="login-password-input" /></div>
              {err && <div className="text-sm text-rose-500" data-testid="login-error">{err}</div>}
              <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">{busy ? "Checking…" : "Continue"}</Button>
              <button type="button" onClick={forgot} className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</button>
            </form>
          )}
          {step === "code" && (
            <form onSubmit={verify} className="space-y-5" data-testid="otp-form">
              <Mail className="h-8 w-8 text-primary" />
              <div><h2 className="text-2xl font-extrabold">Check your email</h2><p className="mt-1 text-sm text-muted-foreground">We sent a 6-digit code to {ch?.email}. It expires in 10 minutes.</p></div>
              <Code value={f.code} onChange={set("code")} />
              {err && <div className="text-sm text-rose-500" data-testid="login-error">{err}</div>}
              <Button type="submit" className="w-full" disabled={busy || f.code.length !== 6} data-testid="otp-submit-button">{busy ? "Verifying…" : "Verify & sign in"}</Button>
              <div className="flex justify-between text-xs"><button type="button" onClick={resend} className="text-primary hover:underline" data-testid="otp-resend-button">Send a new code</button>
                <button type="button" onClick={() => setStep("password")} className="text-muted-foreground hover:underline">Back</button></div>
            </form>
          )}
          {step === "reset" && (
            <form onSubmit={reset} className="space-y-5" data-testid="reset-form">
              <KeyRound className="h-8 w-8 text-primary" />
              <div><h2 className="text-2xl font-extrabold">Reset password</h2><p className="mt-1 text-sm text-muted-foreground">Enter the code sent to your email and choose a new password (10+ characters, letters and numbers).</p></div>
              <Code value={f.code} onChange={set("code")} />
              <Input type="password" placeholder="New password" value={f.newPw} onChange={set("newPw")} data-testid="reset-new-password-input" />
              {err && <div className="text-sm text-rose-500" data-testid="login-error">{err}</div>}
              <Button type="submit" className="w-full" disabled={busy} data-testid="reset-submit-button">Update password</Button>
              <button type="button" onClick={() => setStep("password")} className="text-xs text-muted-foreground hover:underline">Back to sign in</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export function ChangePassword({ onDone, forced }) {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const save = async () => {
    try { await api.post("/auth/change-password", f); toast.success("Password changed"); onDone?.(); } catch (e) { toast.error(await errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="change-password-form">
      {forced && <p className="text-sm text-amber-600 dark:text-amber-400">Please replace your temporary password before continuing.</p>}
      <Input type="password" placeholder="Current password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="current-password-input" />
      <Input type="password" placeholder="New password (10+ chars, letters & numbers)" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="new-password-input" />
      <Button onClick={save} data-testid="change-password-submit">Change password</Button>
    </div>
  );
}
